import React, { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, IconButton, Menu, MenuItem,
  Avatar, Box, Tooltip, Typography, Divider, Badge, Chip,
  List, ListItem, ListItemText, ListItemButton
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  ExitToApp,
  Person as PersonIcon,
  Settings as SettingsIcon,
  CheckCircle,
  Link as LinkIcon,
  LinkOff as LinkOffIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';
import './header.css';

export default function Header({ sidebarWidth = 240 }) {
  const navigate = useNavigate();
  const { notifications, naoLidas, marcarComoLida, marcarTodasComoLidas } = useNotifications();

  const [anchorConta, setAnchorConta] = useState(null);
  const [anchorNotif, setAnchorNotif] = useState(null);
  const [nomeUsuario, setNomeUsuario] = useState('');
  const [permissaoUsuario, setPermissaoUsuario] = useState('');
  const [statusIntegracoes, setStatusIntegracoes] = useState(null);

  useEffect(() => {
    async function fetchUser() {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/usuario/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (res.ok) {
        const data = await res.json();
        setNomeUsuario(data.nome);
        setPermissaoUsuario(data.permissao);
      }
    }
    fetchUser();
  }, []);

  useEffect(() => {
    async function fetchStatusIntegracoes() {
      const token = localStorage.getItem('token');
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/integracoes/status/resumido`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (res.ok) {
          const data = await res.json();
          setStatusIntegracoes(data);
        }
      } catch (error) {
        console.error('Erro ao buscar status das integrações:', error);
      }
    }
    fetchStatusIntegracoes();
    // Atualizar a cada 30 segundos
    const interval = setInterval(fetchStatusIntegracoes, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleContaOpen = e => setAnchorConta(e.currentTarget);
  const handleContaClose = () => setAnchorConta(null);

  const handleNotifOpen = e => setAnchorNotif(e.currentTarget);
  const handleNotifClose = () => setAnchorNotif(null);

  const handleNotificacaoClick = async (notif) => {
    await marcarComoLida(notif.id);
    if (notif.aluguel_id) {
      navigate(`/alugueis/${notif.aluguel_id}`);
    }
    handleNotifClose();
  };

  const goto = path => {
    navigate(path);
    handleContaClose();
  };

  const logout = () => {
    localStorage.removeItem('token');
    window.location.href = '/';
  };

  // Detecta se o sidebar está colapsado lendo do localStorage (usado pelo Sidebar)
  const [sidebarWidthState, setSidebarWidthState] = useState(sidebarWidth);
  useEffect(() => {
    function updateSidebarWidth() {
      // O Sidebar pode salvar o estado de colapso no localStorage
      const collapsed = localStorage.getItem('sidebar-collapsed') === 'true';
      setSidebarWidthState(collapsed ? 72 : 240);
    }
    window.addEventListener('storage', updateSidebarWidth);
    updateSidebarWidth();
    return () => window.removeEventListener('storage', updateSidebarWidth);
  }, []);

  return (
    <AppBar
      position="fixed"
      elevation={0}
      className="header-google"
      sx={{
        background: 'transparent',
        color: '#202124',
        borderBottom: '1px solid #e8eaed',
        zIndex: 1200,
        left: `${sidebarWidthState}px`,
        width: `calc(100% - ${sidebarWidthState}px)`,
        boxShadow: 'none',
        transition: 'all 0.3s ease',
      }}
    >
      <Toolbar sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        minHeight: '64px !important',
        px: 3
      }}>
        {/* Ações do Usuário */}
        <Box display="flex" alignItems="center" gap={1}>
          {/* Status de Integrações */}
          {statusIntegracoes && (
            <Tooltip title={statusIntegracoes.todasConectadas ? 'Todas as integrações conectadas' : 'Clique para configurar integrações'}>
              <IconButton
                onClick={() => navigate('/integracoes')}
                sx={{
                  color: statusIntegracoes.todasConectadas ? '#4caf50' : '#f44336',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                }}
              >
                {statusIntegracoes.todasConectadas ? (
                  <CheckCircle />
                ) : (
                  <LinkOffIcon />
                )}
              </IconButton>
            </Tooltip>
          )}

          {/* Notificações */}
          <Tooltip title="Notificações">
            <IconButton
              onClick={handleNotifOpen}
              sx={{
                color: '#5f6368',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
              }}
            >
              <Badge badgeContent={naoLidas} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Menu de Notificações */}
          <Menu
            anchorEl={anchorNotif}
            open={Boolean(anchorNotif)}
            onClose={handleNotifClose}
            PaperProps={{
              elevation: 3,
              sx: {
                mt: 1.5,
                minWidth: 360,
                maxWidth: 400,
                maxHeight: 500,
                borderRadius: 2
              }
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Notificações
              </Typography>
              {naoLidas > 0 && (
                <IconButton size="small" onClick={marcarTodasComoLidas} title="Marcar todas como lidas">
                  <CheckCircle fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Divider />
            
            {notifications.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Nenhuma notificação
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {notifications.map((notif) => (
                  <ListItem key={notif.id} disablePadding>
                    <ListItemButton
                      onClick={() => handleNotificacaoClick(notif)}
                      sx={{
                        bgcolor: notif.lida ? 'transparent' : 'rgba(25, 118, 210, 0.08)',
                        '&:hover': {
                          bgcolor: notif.lida ? 'rgba(0,0,0,0.04)' : 'rgba(25, 118, 210, 0.12)'
                        }
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={notif.lida ? 400 : 600}>
                            {notif.titulo}
                          </Typography>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {notif.mensagem}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              {new Date(notif.criado_em).toLocaleString('pt-BR')}
                            </Typography>
                          </>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Menu>

          {/* Avatar e Menu */}
          <Tooltip title="Conta">
            <IconButton 
              onClick={handleContaOpen} 
              sx={{ 
                ml: 1,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
              }}
            >
              <Avatar 
                sx={{ 
                  bgcolor: '#1a73e8',
                  width: 32, 
                  height: 32,
                  fontSize: '14px',
                  fontWeight: 500
                }}
              >
                {nomeUsuario ? nomeUsuario.charAt(0).toUpperCase() : 'U'}
              </Avatar>
            </IconButton>
          </Tooltip>

          {/* Menu do Usuário */}
          <Menu 
            anchorEl={anchorConta} 
            open={Boolean(anchorConta)} 
            onClose={handleContaClose}
            PaperProps={{
              elevation: 3,
              sx: {
                mt: 1.5,
                minWidth: 280,
                borderRadius: 2,
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1.5,
                  borderRadius: 1,
                  mx: 1,
                  '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.04)'
                  }
                }
              }
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {/* Cabeçalho do Menu */}
            <Box sx={{ px: 2, py: 2, mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: '#1a73e8',
                    width: 40,
                    height: 40
                  }}
                >
                  {nomeUsuario ? nomeUsuario.charAt(0).toUpperCase() : 'U'}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {nomeUsuario || 'Usuário'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {permissaoUsuario || 'Usuário'}
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Divider />

            <MenuItem onClick={() => goto('/meu-perfil')}>
              <PersonIcon sx={{ mr: 2, color: '#5f6368' }} />
              Meu Perfil
            </MenuItem>

            <MenuItem onClick={() => goto('/configuracoes')}>
              <SettingsIcon sx={{ mr: 2, color: '#5f6368' }} />
              Configurações
            </MenuItem>

            <Divider sx={{ my: 1 }} />

            <MenuItem onClick={logout}>
              <ExitToApp sx={{ mr: 2, color: '#5f6368' }} />
              Sair
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
